import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { UAParser } from 'ua-parser-js';

import { distances } from '../../styles/constants';
import { Dl, Dt, Dd } from '../../components/DescriptionList/Horizontal';
import { H2 } from '../../components/Typography';
import { Session, Transaction } from '../../types/checkout';
import Hr from '../../components/Hr';

export interface DeviceInfoProps {
    transaction?: Transaction;
    session?: Session;
}

const DeviceInfo = (
    {
        transaction,
        session,
    }: DeviceInfoProps
) => {
    const { t } = useTranslation();
    const userAgent = transaction?.user_agent ?? session?.user_agent;
    const ua = userAgent ? new UAParser(userAgent).getResult() : undefined;
    const initiatingSystemRequestHeaders = transaction?.initiating_system_request_headers || session?.initiating_system_request_headers;
    const customerIp = transaction?.customer_ip ?? session?.customer_ip;
    return (
        <Wrapper>
            <H2>{t('payments.transaction.device_info')}</H2>
            <Hr />
            {customerIp && <>
                <Dl>
                    <Dt doubleColumn>{t('payments.fields.customer_ip')}</Dt>
                    <Dd>{customerIp}</Dd>
                </Dl>
                <Spacer />
            </>}
            {ua && (<>
                {ua.browser.name && (
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.browser')}</Dt>
                        <Dd>
                            {ua.browser.name} {ua.browser.version}
                        </Dd>
                    </Dl>
                )}
                {ua.device.model && (
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.device')}</Dt>
                        <Dd>
                            {ua.device.model} {ua.device.type}
                        </Dd>
                    </Dl>
                )}
                {ua.os.name && (
                    <Dl>
                        <Dt doubleColumn>{t('payments.fields.operating_system')}</Dt>
                        <Dd>
                            {ua.os.name} {ua.os.version}
                        </Dd>
                    </Dl>
                )}

                <Spacer />
            </>)}
            {initiatingSystemRequestHeaders && <>
                {([
                    'dintero-system-name',
                    'dintero-system-version',
                    'dintero-system-plugin-name',
                    'dintero-system-plugin-version',
                ] as const).map((field, i) => {
                    const value = initiatingSystemRequestHeaders && initiatingSystemRequestHeaders[field];
                    if (!value) {
                        return undefined;
                    }
                    return (
                        <Dl key={i}>
                            <Dt doubleColumn>{t(`payments.transaction.request_headers.${field}`)}:</Dt>
                            <Dd title={value}>{value}</Dd>
                        </Dl>
                    );
                })}
            </>
            }
        </Wrapper>
    );
};

export default DeviceInfo;

const Wrapper = styled.div`
    padding-top: ${distances.normal};
`;

const Spacer = styled.div`
    height: ${distances.normal};
`;
